import { AiTargetType } from '../../api';
import { ToastContext } from '../../services/toast/toast';
import { BaseArea } from '../base/area/area';
import { BaseButton } from '../base/button/button';
import { Toggle } from '../base/toggle/toggle';
import { mdiContentCopy, mdiDeleteSweep, mdiKey, mdiSend } from '@mdi/js';
import Icon from '@mdi/react';
import { observer } from 'mobx-react';
import React, { useContext, useEffect, useRef, useState } from 'react';
import {
    buildCurl,
    callGateway,
    endpointsForKey,
    GATEWAY_ENDPOINTS,
    IChatMessage,
    IGatewayRequest,
    IGatewayResult,
} from '../../services/ai/ai-gateway.client';
import { AiStore } from '../../stores/ai/ai.store';
import { formatWhen, KEY_STATUS_TONE, NumberField, SelectField, StatusTag, useFieldIds } from './ai-fields';
import * as S from './ai.styles';

const RESULT_LABEL: Record<string, string> = {
    responses: 'Output',
    embeddings: 'Embedding',
    completions: 'Completion',
    models: 'Models',
};

/** Long number arrays (embeddings) would drown the raw view; show the head and say how many were cut. */
const rawReplacer = (_k: string, v: unknown): unknown =>
    Array.isArray(v) && v.length > 16 && typeof v[0] === 'number' ? [...v.slice(0, 8), `… ${v.length - 8} more`] : v;

export const TestingTab = observer(({ store, onIssue }: { store: AiStore; onIssue: () => void }): React.ReactElement => {
    const toast = useContext(ToastContext);
    const systemMessageId = useFieldIds(false).id;
    const keys = store.sessionKeys;

    const [keyId, setKeyId] = useState('');
    const [endpointId, setEndpointId] = useState('chat');
    const [model, setModel] = useState('');
    const [system, setSystem] = useState('');
    const [temperature, setTemperature] = useState<number | ''>('');
    const [maxTokens, setMaxTokens] = useState<number | ''>('');
    const [stream, setStream] = useState(false);
    const [input, setInput] = useState('');
    const [messages, setMessages] = useState<IChatMessage[]>([]);
    const [result, setResult] = useState<{ label: string; text: string } | null>(null);
    const [resp, setResp] = useState<IGatewayResult | null>(null);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [showRaw, setShowRaw] = useState(false);
    const [curlCopied, setCurlCopied] = useState(false);
    const abortRef = useRef<AbortController | null>(null);

    // A request still streaming when the tab is left must not keep writing into state that no longer exists.
    useEffect(() => () => abortRef.current?.abort(), []);

    const key = keys.find(k => k.id === keyId) ?? keys[0];
    const endpoints = key ? endpointsForKey(key) : GATEWAY_ENDPOINTS.slice(0, 1);
    const endpoint = endpoints.find(e => e.id === endpointId) ?? endpoints[0];
    const isAgentKey = key?.targetType === AiTargetType.AGENT;
    const models = key?.models ?? [];
    const effectiveModel = key ? (models.includes(model) ? model : key.defaultModel) : '';
    const streamAllowed = endpoint.id === 'chat' && (!isAgentKey || !!key?.agent?.streamingSupported);
    const baseUrl = key?.baseUrl ?? '';
    const status = key ? store.statusOf(key) : undefined;

    const buildRequest = (text: string): IGatewayRequest => ({
        endpoint,
        model: effectiveModel,
        input: text,
        system: endpoint.id === 'chat' && system ? system : undefined,
        history: messages,
        temperature: temperature === '' ? undefined : temperature,
        maxTokens: maxTokens === '' ? undefined : maxTokens,
        stream: stream && streamAllowed,
    });

    const reset = (): void => {
        abortRef.current?.abort();
        setMessages([]);
        setResult(null);
        setResp(null);
        setError('');
        setInput('');
        setBusy(false);
    };

    // A newly issued key is the one the operator wants to try: switch to it, on the default endpoint, with a clean
    // console. Keyed on the ID so it runs once per issued key, not on every render.
    const issuedKeyId = store.lastIssuedKeyId;
    useEffect(() => {
        if (!issuedKeyId) return;
        setKeyId(issuedKeyId);
        setModel('');
        setEndpointId('chat');
        reset();
    }, [issuedKeyId]);

    const canSend = !!key && !busy && (endpoint.id === 'models' || input.trim() !== '');

    const send = async (): Promise<void> => {
        const secret = key ? store.secretFor(key.id) : undefined;
        if (!key || !secret || !canSend) return;
        if (endpoint.id !== 'models' && !effectiveModel) {
            setError('Model required.');
            return;
        }
        const text = input;
        const prior = messages;
        const user: IChatMessage = { role: 'user', content: text };
        const ac = new AbortController();
        abortRef.current = ac;
        setBusy(true);
        setError('');
        setResp(null);
        setResult(null);
        setShowRaw(false);
        if (endpoint.id === 'chat') {
            setMessages([...prior, user]);
            setInput('');
        }
        try {
            const res = await callGateway({
                baseUrl,
                apiKey: secret,
                request: buildRequest(text),
                signal: ac.signal,
                onText: t => setMessages([...prior, user, { role: 'assistant', content: t }]),
            });
            setResp(res);
            if (endpoint.id === 'chat') setMessages([...prior, user, { role: 'assistant', content: res.text }]);
            else setResult({ label: RESULT_LABEL[endpoint.id] ?? 'Result', text: res.text || 'No output.' });
        } catch (e) {
            if ((e as Error)?.name === 'AbortError') return;
            // The message carries the status and the gateway's own wording; the key is never part of it.
            setError((e as Error)?.message || 'Request failed.');
        } finally {
            if (abortRef.current === ac) setBusy(false);
        }
    };

    const curl = key ? buildCurl(baseUrl, buildRequest(input || '…')) : '';
    const copyCurl = async (): Promise<void> => {
        try {
            await navigator.clipboard.writeText(curl);
            setCurlCopied(true);
            setTimeout(() => setCurlCopied(false), 1500);
        } catch {
            toast.notify?.('Failed to copy to clipboard', 'right', 'warning');
        }
    };

    return (
        <S.TestLayout>
            <S.RequestCard>
                <S.CardHead>
                    <S.Grow>
                        <S.CardTitle>Request</S.CardTitle>
                    </S.Grow>
                    {/* Always available: another key is often wanted while one exists (a different profile, or a
                        fresh one after the first expires). */}
                    <BaseButton variant="outlined" size="compact" onClick={onIssue}>
                        <S.ButtonContent>
                            <Icon path={mdiKey} size={0.65} />
                            Issue key
                        </S.ButtonContent>
                    </BaseButton>
                    <StatusTag tone="primary">Live · gateway</StatusTag>
                </S.CardHead>

                {keys.length === 0 ? (
                    <S.Empty>
                        No keys with a secret in this session. Secrets are only returned at issuance and kept in
                        memory. Use Issue key to create one.
                    </S.Empty>
                ) : (
                    <>
                        <SelectField
                            label="Key · issued this session"
                            value={key?.id}
                            options={keys.map(k => ({ value: k.id, label: `${k.profileId} · ${k.id.slice(0, 8)} · ${store.statusOf(k)}` }))}
                            onChange={(v): void => {
                                setKeyId(v);
                                setModel('');
                                reset();
                            }}
                        />
                        {key && status && (
                            <S.InlineRow>
                                <StatusTag tone={KEY_STATUS_TONE[status]}>{status}</StatusTag>
                                <S.Muted>
                                    {key.targetType} · expires {formatWhen(key.expiresAt)}
                                </S.Muted>
                            </S.InlineRow>
                        )}
                    </>
                )}

                <S.Field>
                    <S.Label>Base URL</S.Label>
                    <S.Pre>{baseUrl || '—'}</S.Pre>
                </S.Field>
                <S.FormGrid>
                    <SelectField
                        label="Endpoint"
                        value={endpoint.id}
                        // Not while a request is out: its reply and status would land under the new endpoint's URL.
                        disabled={busy}
                        options={endpoints.map(e => ({ value: e.id, label: e.label }))}
                        onChange={(v): void => {
                            setEndpointId(v);
                            setResult(null);
                            setResp(null);
                            setError('');
                        }}
                    />
                    <SelectField
                        label="Model"
                        value={effectiveModel}
                        disabled={isAgentKey || !key}
                        options={(models.length ? models : ['—']).map(m => ({ value: m, label: m }))}
                        onChange={setModel}
                    />
                </S.FormGrid>

                {endpoint.id === 'chat' && (
                    <S.Field>
                        <S.Label htmlFor={systemMessageId}>System message · optional</S.Label>
                        <BaseArea id={systemMessageId} rows={2} value={system} onChange={(e): void => setSystem(e.target.value)} />
                    </S.Field>
                )}
                {['chat', 'responses', 'completions'].includes(endpoint.id) && (
                    <S.FormGrid>
                        <NumberField label="Temperature" min={0} max={2} step={0.1} placeholder="Default" value={temperature} onChange={setTemperature} />
                        <NumberField label="Max tokens" min={1} placeholder="Default" value={maxTokens} onChange={setMaxTokens} />
                    </S.FormGrid>
                )}
                {endpoint.id === 'chat' && (
                    <S.CardHead>
                        <S.Grow>
                            <S.Strong id="ai-test-stream-label">Stream</S.Strong>
                            <S.Hint>{streamAllowed ? 'Server-sent events.' : 'Agent card does not advertise streaming.'}</S.Hint>
                        </S.Grow>
                        {/* Named by the visible "Stream" text, so the spoken name cannot drift from the screen. */}
                        <Toggle
                            aria-labelledby="ai-test-stream-label"
                            value={stream && streamAllowed}
                            disabled={!streamAllowed}
                            onChange={setStream}
                        />
                    </S.CardHead>
                )}

                <S.Field>
                    <S.InlineRow>
                        <S.Label as="span">cURL</S.Label>
                        <S.Grow />
                        <BaseButton variant="outlined" size="compact" disabled={!key} onClick={(): void => void copyCurl()}>
                            <S.ButtonContent>
                                <Icon path={mdiContentCopy} size={0.6} />
                                {curlCopied ? 'Copied' : 'Copy'}
                            </S.ButtonContent>
                        </BaseButton>
                    </S.InlineRow>
                    <S.Pre>{curl || '—'}</S.Pre>
                </S.Field>
            </S.RequestCard>

            <S.ConsoleCard>
                <S.ConsoleBar>
                    <S.Method>{endpoint.method}</S.Method>
                    <S.Grow>
                        <S.Mono>{baseUrl + endpoint.path}</S.Mono>
                    </S.Grow>
                    <BaseButton variant="outlined" size="compact" onClick={reset}>
                        <S.ButtonContent>
                            <Icon path={mdiDeleteSweep} size={0.65} />
                            Clear
                        </S.ButtonContent>
                    </BaseButton>
                </S.ConsoleBar>

                <S.Messages>
                    {messages.map((m, i) => (
                        <S.Bubble key={i} mine={m.role === 'user'}>
                            <span>{m.role === 'user' ? 'User' : effectiveModel || 'Assistant'}</span>
                            <div>{m.content || '…'}</div>
                        </S.Bubble>
                    ))}
                    {result && (
                        <S.Field>
                            <S.Muted>{result.label}</S.Muted>
                            <S.ResultBox>{result.text}</S.ResultBox>
                        </S.Field>
                    )}
                    {busy && <S.Muted>Waiting for gateway…</S.Muted>}
                    {!busy && !messages.length && !result && !error && <S.Muted>No requests.</S.Muted>}
                    {error && <S.ErrorText>{error}</S.ErrorText>}
                </S.Messages>

                {resp && (
                    <>
                        <S.ResponseBar>
                            <StatusTag tone={resp.status < 300 ? 'success' : 'danger'}>{resp.status} OK</StatusTag>
                            <span>{resp.ms} ms</span>
                            {resp.usage && (
                                <span>
                                    {resp.usage.input} in · {resp.usage.output} out · {resp.usage.total} tokens
                                </span>
                            )}
                            <S.Grow />
                            <BaseButton variant="outlined" size="compact" onClick={(): void => setShowRaw(!showRaw)}>
                                {showRaw ? 'Hide JSON' : 'Raw JSON'}
                            </BaseButton>
                        </S.ResponseBar>
                        {showRaw && <S.Pre>{JSON.stringify(resp.json, rawReplacer, 2)}</S.Pre>}
                    </>
                )}

                <S.Composer>
                    {endpoint.id === 'models' ? (
                        <S.Muted>Lists models this key can call.</S.Muted>
                    ) : (
                        <BaseArea
                            rows={3}
                            value={input}
                            // A placeholder is not a name (it vanishes on typing), so the box is also named for screen readers.
                            aria-label={endpoint.id === 'chat' ? 'Message' : endpoint.id === 'embeddings' ? 'Text to embed' : 'Prompt'}
                            placeholder={endpoint.id === 'chat' ? 'Message' : endpoint.id === 'embeddings' ? 'Text to embed' : 'Prompt'}
                            onChange={(e): void => setInput(e.target.value)}
                            onKeyDown={(e): void => {
                                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                                    e.preventDefault();
                                    void send();
                                }
                            }}
                        />
                    )}
                    <S.Button variant="primary" disabled={!canSend} onClick={(): void => void send()}>
                        <S.ButtonContent>
                            <Icon path={mdiSend} size={0.75} />
                            Send
                        </S.ButtonContent>
                    </S.Button>
                </S.Composer>
            </S.ConsoleCard>
        </S.TestLayout>
    );
});
